import { useState, useRef, useEffect, useCallback } from 'react'
import { Zap, Loader2, Search, DollarSign, Smartphone, ChevronDown, X, Info } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs'
import { Badge } from '@/app/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import { SearchFilter } from '@/app/components/ui/SearchFilter'
import { toast } from 'sonner'
import { useQuery, useMutation } from '@tanstack/react-query'
import { wbsService, WbsProduct } from '@/app/services/wbs.service'
import { useDebounce } from '@/hooks/useDebounce'

interface WbsRechargeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WbsRechargeModal({ open, onOpenChange }: WbsRechargeModalProps) {
  const [activeTab, setActiveTab] = useState('recharge')

  // Form states
  const [selectedProduct, setSelectedProduct] = useState<WbsProduct | null>(null)
  const [amount, setAmount] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [folio, setFolio] = useState('')
  const [balanceDetails, setBalanceDetails] = useState<any>(null)

  // Polling / Transaction states
  const [txStatus, setTxStatus] = useState<'IDLE' | 'SENDING' | 'PENDING' | 'SUCCESS' | 'FAILED'>('IDLE')
  const [activeFolio, setActiveFolio] = useState<string>('')
  const [pollCount, setPollCount] = useState(0)

  // History states
  const [hSearch, setHSearch] = useState('')
  const [hPage, setHPage] = useState(1)
  const debouncedHSearch = useDebounce(hSearch, 400)

  // Derived state for service check
  const isService = selectedProduct?.group?.toUpperCase().includes('SERVICIO') || selectedProduct?.group?.toUpperCase().includes('PAYMENT') || false;
  // Make amount readonly only if it's a strict fixed amount and not a service that might need checking
  const isAmountFixed = selectedProduct !== null && !selectedProduct.check_amount && Number(selectedProduct.amount) > 0 && !isService;

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        handleReset()
        setActiveTab('recharge')
      }, 300)
    }
  }, [open])

  // Product selector states
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [allProducts, setAllProducts] = useState<WbsProduct[]>([])
  const [hasMore, setHasMore] = useState(true)
  const debouncedSearch = useDebounce(searchInput, 300)
  const listRef = useRef<HTMLDivElement>(null)
  const selectorRef = useRef<HTMLDivElement>(null)

  // Reset products when search changes
  useEffect(() => {
    setPage(1)
    setAllProducts([])
    setHasMore(true)
  }, [debouncedSearch])

  // Close selector on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setSelectorOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { isFetching: isFetchingProducts, data: productsQueryData } = useQuery({
    queryKey: ['wbs-products', debouncedSearch, page],
    queryFn: () => wbsService.getProducts({ search: debouncedSearch, page, per_page: 20 }),
    enabled: open,
    staleTime: 1000 * 60 * 5,
    placeholderData: (prev: any) => prev,
  })

  // TanStack Query v5: onSuccess fue eliminado; usamos useEffect para acumular páginas.
  useEffect(() => {
    if (!productsQueryData) return
    const newItems: WbsProduct[] = (productsQueryData as any)?.data?.data ?? []
    const lastPage: number = (productsQueryData as any)?.data?.last_page ?? 1
    setAllProducts((prev) => page === 1 ? newItems : [...prev, ...newItems])
    setHasMore(page < lastPage)
  }, [productsQueryData])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = listRef.current
    if (!el || isFetchingProducts || !hasMore) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      setPage((p) => p + 1)
    }
  }, [isFetchingProducts, hasMore])

  const handleSelectProduct = (p: WbsProduct) => {
    setSelectedProduct(p)
    // Pre-fill amount if the product has a fixed amount
    if (!p.check_amount && p.amount !== '0') {
      setAmount(p.amount)
    } else {
      setAmount('')
    }
    setSelectorOpen(false)
    setSearchInput('')
  }

  const clearProduct = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedProduct(null)
    setAmount('')
    setAllProducts([])
    setPage(1)
    setHasMore(true)
  }

  // Get Balance Mutation
  const checkBalanceMutation = useMutation({
    mutationFn: wbsService.getBalance,
    onSuccess: (res: any) => {
      const raw = res?.data
      setBalanceDetails(raw)
      toast.success('Saldo actualizado correctamente.')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Error al obtener el saldo.')
    }
  })

  // Check Debt Mutation
  const checkDebtMutation = useMutation({
    mutationFn: wbsService.checkDebt,
    onSuccess: (res: any) => {
      const data = res?.data;
      // WBS suele devolver el monto en 'Amount', 'Price', 'Monto' o similar.
      const debtAmount = data?.Amount || data?.Price || data?.Monto || data?.amount;

      if (debtAmount !== undefined && debtAmount !== null) {
        setAmount(String(debtAmount));
        toast.success(`Adeudo encontrado: $${debtAmount}`);
      } else {
        toast.success('Consulta exitosa, pero la operadora no devolvió un monto específico.');
        console.log('Check Debt Response:', data);
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Error al consultar el adeudo.')
    }
  })

  // Check Status Mutation (Polling)
  const checkStatusMutation = useMutation({
    mutationFn: wbsService.checkStatus,
    onSuccess: (res: any) => {
      const tx = res?.data?.transaction
      if (res.status === 202 || tx?.status === 'PENDING' || tx?.status === 'CONSULTING') {
        setPollCount(p => p + 1)
      } else {
        if (tx?.status === 'SUCCESS') {
          setTxStatus('SUCCESS')
          toast.success('Recarga confirmada exitosamente.')
        } else {
          setTxStatus('FAILED')
          toast.error(tx?.wbs_description || 'La transacción falló o fue declinada.')
        }
      }
    },
    onError: (err: any) => {
      // Si falla la red durante el polling, seguimos intentando hasta timeout
      setPollCount(p => p + 1)
      console.error('Error al consultar estado:', err)
    }
  })

  // Polling Effect
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    if (txStatus === 'PENDING' && activeFolio) {
      if (pollCount > 45) { // 45 intentos * 2s = 90s
        setTxStatus('FAILED')
        toast.error('Tiempo de espera agotado. La transacción se considera declinada.')
        return
      }

      timeoutId = setTimeout(() => {
        checkStatusMutation.mutate(activeFolio)
      }, 2000)
    }
    return () => clearTimeout(timeoutId)
  }, [txStatus, activeFolio, pollCount])

  // Get Transactions Query
  const { data: transactionsQuery, isFetching: isFetchingHistory } = useQuery({
    queryKey: ['wbs-transactions', debouncedHSearch, hPage],
    queryFn: () => wbsService.getTransactions({ search: debouncedHSearch, page: hPage, per_page: 8 }),
    enabled: open && activeTab === 'history',
    placeholderData: (prev: any) => prev,
  })

  // Initiate Transaction Mutation
  const initiateMutation = useMutation({
    mutationFn: wbsService.initiateTransaction,
    onSuccess: (res: any) => {
      const tx = res?.data?.transaction
      if (res.status === 202 || tx?.status === 'PENDING' || tx?.status === 'SENDING') {
        setTxStatus('PENDING')
        setActiveFolio(tx?.folio_pos || '')
        setPollCount(0)
      } else {
        setTxStatus('SUCCESS')
        toast.success(`Recarga exitosa. Folio: ${tx?.wbs_folio || 'N/A'}`)
      }
    },
    onError: (err: any) => {
      setTxStatus('FAILED')
      toast.error(err?.response?.data?.message || 'Error al iniciar la recarga.')
    }
  })

  const handleRecharge = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProduct || !amount || !phoneNumber) {
      toast.error('Por favor completa todos los campos para la recarga.')
      return
    }

    // Validación estricta de 10 dígitos numéricos para recargas
    if (!isService && !/^\d{10}$/.test(phoneNumber)) {
      toast.error('Número inválido. Debes ingresar un número de teléfono de exactamente 10 dígitos.');
      return;
    }
    // Hardcoded sucursal_id 1 for now (assuming it will be derived from auth context in real app)
    setTxStatus('SENDING')
    initiateMutation.mutate({
      type: isService ? 'SERVICE_PAYMENT' : 'RECHARGE',
      carrier_id: selectedProduct.carrier_id,
      amount: amount,
      reference: phoneNumber,
      payment_method: paymentMethod,
      sucursal_id: 1,
    })
  }

  const handleReset = () => {
    setTxStatus('IDLE')
    setSelectedProduct(null)
    setAmount('')
    setPhoneNumber('')
    setPaymentMethod('cash')
    setActiveFolio('')
    setPollCount(0)
  }

  const handleCheckTransactionManual = (e: React.FormEvent) => {
    e.preventDefault()
    if (!folio) {
      toast.error('Por favor ingresa un número de folio.')
      return
    }
    setIsSubmitting(true)
    wbsService.checkStatus(folio)
      .then((res: any) => {
        const tx = res?.data?.transaction
        toast.success(`Estado: ${tx?.status} - ${tx?.wbs_description || ''}`)
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || 'Error al verificar la transacción.')
      })
      .finally(() => setIsSubmitting(false))
  }

  // Quick amount presets (only shown when the product allows custom amount)
  const amountPresets = ['20', '50', '100', '200', '300', '500']

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-100">
              <Zap className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Recargas y Servicios</DialogTitle>
              <DialogDescription>Gestión de saldo, tiempo aire y pagos.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="recharge">Recargas</TabsTrigger>
            <TabsTrigger value="transaction">Transacción</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
            {/* Ocultamos saldo pero conservamos el trigger si se necesita */}
            <TabsTrigger value="balance" className="hidden">Saldo</TabsTrigger>
          </TabsList>

          {/* ── RECARGAS TAB ── */}
          <TabsContent value="recharge" className="pt-4">
            <form onSubmit={handleRecharge} className="space-y-4">

              {/* Product selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Compañía / Servicio</label>
                <div ref={selectorRef} className="relative">
                  {/* Trigger */}
                  <button
                    type="button"
                    onClick={() => setSelectorOpen((v) => !v)}
                    className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  >
                    <span className={selectedProduct ? 'text-slate-900 font-medium' : 'text-slate-400'}>
                      {selectedProduct
                        ? `${selectedProduct.description} — $${selectedProduct.amount} MXN`
                        : 'Seleccionar servicio...'}
                    </span>
                    <span className="flex items-center gap-1 ml-2 shrink-0">
                      {selectedProduct && (
                        <X
                          className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600"
                          onClick={clearProduct}
                        />
                      )}
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${selectorOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </button>

                  {/* Dropdown */}
                  {selectorOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
                      {/* Search input */}
                      <div className="border-b border-slate-100 p-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <input
                            autoFocus
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Buscar compañía o servicio..."
                            className="w-full rounded-sm border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs outline-none focus:border-orange-400"
                          />
                        </div>
                      </div>

                      {/* List */}
                      <div
                        ref={listRef}
                        onScroll={handleScroll}
                        className="max-h-56 overflow-y-auto"
                      >
                        {allProducts.length === 0 && !isFetchingProducts && (
                          <p className="p-3 text-center text-xs text-slate-400">
                            {searchInput ? 'Sin resultados para tu búsqueda.' : 'Escribe para buscar servicios.'}
                          </p>
                        )}
                        {allProducts.map((p, i) => (
                          <button
                            key={`${p.carrier_id}-${p.amount}-${i}`}
                            type="button"
                            onClick={() => handleSelectProduct(p)}
                            className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-orange-50 border-b border-slate-50 last:border-0"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="font-medium text-slate-800 truncate">{p.description}</span>
                              <span className="text-xs text-slate-400">{p.group}</span>
                            </div>
                            <span className="ml-3 shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                              ${p.amount}
                            </span>
                          </button>
                        ))}
                        {isFetchingProducts && (
                          <div className="flex justify-center py-2">
                            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product detail card */}
                {selectedProduct?.note && (
                  <div className="flex gap-2 rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
                    <p className="leading-relaxed">{selectedProduct.note}</p>
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Monto</label>
                <div className="flex flex-col gap-2">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Ej. 100.00"
                      className="pl-9 font-medium"
                      value={amount}
                      readOnly={isAmountFixed}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                  {/* Quick presets — only if product allows custom amount or nothing selected */}
                  {(!selectedProduct || !isAmountFixed) && (
                    <div className="flex flex-wrap gap-2">
                      {amountPresets.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setAmount(preset)}
                          className={`rounded-md border px-3 py-1 text-xs font-medium transition-colors ${amount === preset
                            ? 'bg-orange-100 border-orange-200 text-orange-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                          ${preset}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  {isService ? 'Referencia de Servicio' : 'Número de Teléfono (10 dígitos)'}
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="text"
                    placeholder={isService ? 'Ej. 01234567890' : 'Ej. 5512345678'}
                    className="pl-9"
                    value={phoneNumber}
                    maxLength={isService ? undefined : 10}
                    onChange={(e) => {
                      const val = e.target.value
                      // Solo permitir números si es recarga (opcional, pero ayuda a la UX)
                      if (!isService) {
                        setPhoneNumber(val.replace(/\D/g, ''))
                      } else {
                        setPhoneNumber(val)
                      }
                    }}
                  />
                </div>
                {/* Botón para consultar adeudo (solo servicios) */}
                {isService && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-xs"
                    onClick={() => {
                      if (!selectedProduct) return
                      if (!phoneNumber) {
                        toast.error('Ingresa la referencia para consultar el adeudo.')
                        return
                      }
                      checkDebtMutation.mutate({ carrier_id: selectedProduct.carrier_id, reference: phoneNumber })
                    }}
                    disabled={checkDebtMutation.isPending || !phoneNumber}
                  >
                    {checkDebtMutation.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Search className="mr-2 h-3.5 w-3.5" />
                    )}
                    Consultar Adeudo
                  </Button>
                )}
              </div>

              {/* Método de Pago */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Método de Pago</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                >
                  <option value="cash">Efectivo</option>
                  <option value="card">Tarjeta de Crédito/Débito</option>
                  <option value="transfer">Transferencia</option>
                </select>
                <p className="text-xs text-slate-500">
                  Esta recarga registrará una venta en caja con el método seleccionado.
                </p>
              </div>

              <div className="pt-2">
                {txStatus === 'IDLE' ? (
                  <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={initiateMutation.isPending}>
                    {initiateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                    Confirmar Recarga
                  </Button>
                ) : txStatus === 'SENDING' ? (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-orange-50 border border-orange-100">
                    <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                    <p className="text-sm text-orange-800 font-medium">Iniciando transacción...</p>
                  </div>
                ) : txStatus === 'PENDING' ? (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-blue-50 border border-blue-100">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    <p className="text-sm text-blue-800 font-medium">Confirmando transacción con el proveedor...</p>
                    <p className="text-xs text-blue-600">Esto puede tomar hasta 90 segundos. ({pollCount}/45)</p>
                  </div>
                ) : txStatus === 'SUCCESS' ? (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-emerald-50 border border-emerald-100">
                    <div className="text-emerald-600 font-medium text-sm text-center">¡Recarga Exitosa!</div>
                    <Button type="button" variant="outline" className="w-full mt-2" onClick={handleReset}>
                      Nueva Recarga
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-red-50 border border-red-100">
                    <div className="text-red-600 font-medium text-sm text-center">La recarga falló.</div>
                    <Button type="button" variant="outline" className="w-full mt-2" onClick={handleReset}>
                      Intentar Nuevamente
                    </Button>
                  </div>
                )}
              </div>
            </form>
          </TabsContent>

          {/* ── TRANSACCIÓN TAB ── */}
          <TabsContent value="transaction" className="pt-4">
            <form onSubmit={handleCheckTransactionManual} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Folio de Operación</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Ej. 10008110201031234"
                    className="pl-9"
                    value={folio}
                    onChange={(e) => setFolio(e.target.value)}
                  />
                </div>
                <p className="text-xs text-slate-500">Ingresa el folio para verificar si la recarga fue exitosa.</p>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Verificar Transacción
              </Button>
            </form>
          </TabsContent>

          {/* ── SALDO TAB ── */}
          <TabsContent value="balance" className="pt-4">
            <div className="flex flex-col items-center space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-6">
              <div className="rounded-full bg-emerald-100 p-3">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="text-center w-full">
                <p className="text-sm font-medium text-slate-500 mb-1">Saldo Disponible</p>
                <h3 className={`font-bold ${balanceDetails?.Balance ? 'text-4xl text-emerald-700' : 'text-xl text-slate-400'}`}>
                  {balanceDetails?.Balance ? `$${balanceDetails.Balance} MXN` : '---'}
                </h3>
              </div>

              {balanceDetails && (
                <div className="w-full rounded-md border border-slate-200 bg-white divide-y divide-slate-100 text-sm overflow-hidden">
                  {[
                    { label: 'Saldo Inicial', value: balanceDetails.Saldo_Inicial },
                    { label: 'Compras', value: balanceDetails.Compras },
                    { label: 'Ventas', value: balanceDetails.Ventas },
                    { label: 'Comisión', value: balanceDetails.Comision },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between px-3 py-2">
                      <span className="text-slate-500">{label}</span>
                      <span className="font-medium text-slate-800">${value ?? '0'}</span>
                    </div>
                  ))}
                  {balanceDetails.Confirmation && (
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                      <span className="text-xs text-slate-400">Confirmación</span>
                      <span className="text-xs font-mono text-slate-500">{balanceDetails.Confirmation}</span>
                    </div>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => checkBalanceMutation.mutate()}
                disabled={checkBalanceMutation.isPending}
                className="w-full"
              >
                {checkBalanceMutation.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : 'Consultar Saldo Actual'}
              </Button>
            </div>
          </TabsContent>

          {/* ── HISTORIAL TAB ── */}
          <TabsContent value="history" className=" max-w-[500px] min-w-0 pt-4 flex flex-col gap-4 h-[300px] overflow-hidden">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <div className="relative flex-1 max-w-xs">
                <SearchFilter
                  placeholder="Buscar por folio o ref..."
                  initialValue={hSearch}
                  onSearch={(val) => {
                    setHSearch(val)
                    setHPage(1)
                  }}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-10 shrink-0"
                onClick={() => {
                  setHPage(1)
                  transactionsQuery.refetch()
                }}
                disabled={isFetchingHistory}
              >
                {isFetchingHistory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Actualizar
              </Button>
            </div>

            <div className="w-full max-w-full overflow-hidden rounded-md border border-slate-200 bg-white">
              <Table className="w-fit">
                <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isFetchingHistory && !transactionsQuery?.data?.data?.length ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-slate-500">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Cargando historial...
                      </TableCell>
                    </TableRow>
                  ) : !transactionsQuery?.data?.data?.length ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-slate-500">
                        No hay transacciones registradas.
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactionsQuery.data.data.map((tx: any) => (

                      <TableRow key={tx.id}>
                        <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                          {new Date(tx.created_at).toLocaleString('es-MX', {
                            day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="font-medium text-xs">
                          {tx.carrier_id}
                          <div className="text-[10px] text-slate-400">Folio: {tx.wbs_folio || tx.folio_pos}</div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{tx.reference}</TableCell>
                        <TableCell className="text-xs font-semibold">${tx.amount}</TableCell>
                        <TableCell>
                          {tx.status === 'SUCCESS' ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Exitoso</Badge>
                          ) : tx.status === 'PENDING' ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pendiente</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Fallido</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* Paginación global */}
              {transactionsQuery?.data && (
                <GlobalPaginator
                  totalItems={transactionsQuery.data.total}
                  currentPage={transactionsQuery.data.current_page}
                  itemsPerPage={transactionsQuery.data.per_page}
                  from={(transactionsQuery.data.current_page - 1) * transactionsQuery.data.per_page + 1}
                  to={Math.min(transactionsQuery.data.current_page * transactionsQuery.data.per_page, transactionsQuery.data.total)}
                  onPageChange={(page) => setHPage(page)}
                />
              )}
            </div>


          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
